import React from 'react';
import TextWinds from '../../../components/common/TextWinds';
import { useAppDispatch } from '../../../store/hook';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import instance from '../../../api/axios/utils.ts/instance';
import {
  closeModal,
  openAlert,
  openModal,
} from '../../../store/features/modalSlice';
import HFinput from '../../../components/common/input/HFinput';
import HFinputPhone from '../../../components/common/input/HFinputPhone';
import HFbutton from '../../../components/button/HFbutton';
import { handleCopyClipBoard } from '../../../api/utils/util-func';
import { timeLeftCheck, timeLeftToString } from '../../../controllers/useTimer';
import useSmsHook from '../../../controllers/useSmsHook';
import { isValidPhone, normalizePhone } from '../../../constants/validation';

type resetPwByPhoneDTO = {
  uid: string;
  name: string;
  verifySmsId: string;
  phone: string;
  verifyType: 'PHONE';
};

const LoginPasswordPhone = () => {
  const dispatch = useAppDispatch();
  const navigete = useNavigate();
  const [verifyNumber, setVerifyNumber] = React.useState<string>('');
  const [isConfirmPhone, setIsConfirmPhone] = React.useState<boolean>(false);
  const [isConfirmCode, setIsConfirmCode] = React.useState<boolean>(false);
  const [isPossibleNext, setIsPossibleNext] = React.useState<boolean>(false);

  const { sendVerifyNumberWithName, checkVerifyNumber, timeLeft } =
    useSmsHook();

  const [searchParam] = useSearchParams();
  const uid = searchParam.get('uid');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<resetPwByPhoneDTO>({ defaultValues: { uid: uid } });

  const name = watch('name');

  const openAlertWithMessage = (msg: string) => {
    dispatch(openAlert({ message: msg }));
  };

  const onSubmit = (data: resetPwByPhoneDTO) => {
    data.verifyType = 'PHONE';

    if (data.verifySmsId === undefined) {
      dispatch(openAlert({ message: '전화번호 인증을 받아주세요.' }));
      return;
    }

    const params: resetPwByPhoneDTO = {
      uid: data.uid,
      name: data.name,
      phone: normalizePhone(data.phone),
      verifyType: 'PHONE',
      verifySmsId: data.verifySmsId,
    };

    getUserId(params);
  };

  // 인증번호 발송
  const sendSmsHandler = () => {
    setIsConfirmPhone(true);

    // uid 입력 여부
    const uid = watch('uid');
    if (!uid) {
      dispatch(
        openAlert({
          message: '아이디를 입력해주세요.',
        }),
      );
      setIsConfirmPhone(false);
      return;
    }

    // 이름 유효성 검사
    const regName = /^[가-힣]{2,}$/;
    if (!regName.test(name)) {
      dispatch(
        openAlert({
          message: '이름은 공백 없이 한글 2글자 이상 입력해주세요.',
        }),
      );
      setIsConfirmPhone(false);
      return;
    }

    // 휴대폰 번호 유효성 검사
    if (!isValidPhone(watch('phone'))) {
      dispatch(
        openAlert({
          message: '휴대폰 번호는 숫자 01012345678 형식으로 입력해주세요.',
        }),
      );
      setIsConfirmPhone(false);
      return;
    }

    sendVerifyNumberWithName(watch('phone'), name).then((msg) => {
      setIsConfirmPhone(false);
      openAlertWithMessage(msg);
    });
  };

  // 인증번호 확인
  const confirmSmsHandler = () => {
    setIsConfirmCode(true);

    checkVerifyNumber(watch('phone'), verifyNumber).then((res) => {
      if (res.confirm) {
        setIsConfirmPhone(true);
        setIsConfirmCode(true);
        setIsPossibleNext(true);
        setValue('verifySmsId', res.confirm.toString());
      } else {
        setIsConfirmCode(false);
      }
      openAlertWithMessage(res.msg);
    });
  };

  const getUserId = async (params: resetPwByPhoneDTO) => {
    try {
      const res = await instance.patch(
        '/v1/mypage/user/reset-password',
        params,
      );

      if (res === undefined) {
        dispatch(openAlert({ message: '회원정보가 일치하지 않습니다.' }));
        return;
      }

      dispatch(
        openModal({
          body: (
            <ViewPwModal
              pw={res.data.data.password}
              linkTo={(link) => navigete(link)}
            />
          ),
        }),
      );
    } catch (err) {
      dispatch(openAlert({ message: '아이디를 확인해주세요.' }));
    }
  };

  return (
    <form>
      <TextWinds
        type="caption1"
        className="mb-5 rounded bg-gray2 p-2 text-center"
      >
        학생 계정의 비밀번호 초기화는 클래스 선생님의 LMS를 통해 초기화 할 수
        있습니다.
      </TextWinds>
      <HFinput
        label="아이디"
        name="uid"
        register={register('uid', {
          required: { value: true, message: '아이디를 입력해주세요.' },
        })}
        placeholder="아이디를 입력해주세요."
        error={errors.uid?.message}
      />
      <HFinput
        label="이름"
        name="name"
        register={register('name', {
          required: { value: true, message: '이름을 입력해주세요.' },
        })}
        placeholder="이름을 입력해주세요."
        error={errors.name?.message}
        disabled={timeLeftCheck(timeLeft) || isConfirmCode}
      />
      <div className="relative">
        <HFinputPhone
          label="휴대폰 번호"
          placeholder="휴대폰 번호를 입력해주세요."
          name="phone"
          register={register('phone', {
            required: { value: true, message: '휴대폰 번호를 입력해주세요.' },
          })}
          disabled={timeLeftCheck(timeLeft) || isConfirmCode}
        />
        <div className="absolute right-1 top-8">
          <HFbutton
            height={40}
            onClick={sendSmsHandler}
            disabled={timeLeftCheck(timeLeft) || isConfirmPhone}
          >
            {timeLeftCheck(timeLeft) ? timeLeftToString(timeLeft) : '인증번호'}
          </HFbutton>
        </div>
      </div>
      <div className="relative">
        <HFinput
          label="인증번호"
          type="text"
          placeholder="인증번호를 입력해주세요."
          value={verifyNumber}
          onChange={(e) => setVerifyNumber(e.target.value)}
          disabled={isConfirmCode}
        />
        <div className="absolute right-1 top-8">
          <HFbutton
            height={40}
            onClick={confirmSmsHandler}
            disabled={isConfirmCode}
          >
            {isConfirmCode ? '인증완료' : '인증확인'}
          </HFbutton>
        </div>
      </div>

      <div className="flex gap-3">
        <HFbutton type="primary-outlined" onClick={() => navigete('/signin')}>
          취소
        </HFbutton>
        <HFbutton onClick={handleSubmit(onSubmit)} disabled={!isPossibleNext}>
          확인
        </HFbutton>
      </div>
    </form>
  );
};

const ViewPwModal = ({
  pw,
  linkTo,
}: {
  pw: string;
  linkTo: (link: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const closeHandler = () => {
    dispatch(closeModal());
    linkTo('/signin');
  };

  const copyHandler = () => {
    handleCopyClipBoard(pw).then(() => {
      alert('복사되었습니다.');
    });
  };
  return (
    <div className="w-full max-w-[480px] rounded bg-white p-5">
      <TextWinds type="title_h1" className="text-center">
        비밀번호 초기화 완료
      </TextWinds>

      <div className="mt-5 pb-5 text-center text-sm">
        임시 비밀번호는 <span className="font-semibold">{pw}</span> 입니다.
        <br />
        임시 비밀번호로 로그인 후,{' '}
        <span className="font-semibold">[내 정보]</span>에서 비밀번호를 변경하실
        수 있습니다.
        {/* 비밀번호는 <span className="font-semibold">{pw}</span> 입니다. */}
      </div>
      <div className="flex gap-3">
        <HFbutton onClick={copyHandler}>복사하기</HFbutton>
        <HFbutton type="primary-outlined" onClick={closeHandler}>
          닫기
        </HFbutton>
      </div>
    </div>
  );
};

export default LoginPasswordPhone;
